import { useEffect, useState } from 'react';
import type { AccountSessionInfo } from '@kassa/shared';
import { api, ApiError } from '../api';
import { useConfirm } from '../components/ConfirmDialog';

// Açıq sessiyalar (KT-94, «Hesabım»): hansı cihazlarda daxil olunub; itmiş telefonu bağlamaq, digər cihazlardan çıxmaq.

// Vaxt Bakı ilə: dd.mm.yyyy HH:MM
const BAKU = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Baku', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
function when(iso: string | null): string {
  if (!iso) return '—';
  const p = Object.fromEntries(BAKU.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}

export function SessionsPanel() {
  const confirm = useConfirm();
  const [items, setItems] = useState<AccountSessionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .accountSessions()
      .then(setItems)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Sessiyalar yüklənmədi'));

  useEffect(() => {
    void load();
  }, []);

  async function act(action: () => Promise<string | null>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı');
    } finally {
      setBusy(false);
    }
  }

  const closeOne = async (s: AccountSessionInfo) => {
    if (!(await confirm(`«${s.device ?? 'Naməlum cihaz'}» sessiyası bağlansın? O cihazda yenidən daxil olmaq lazım olacaq.`, { confirmLabel: 'Bağla', danger: true }))) return;
    void act(async () => {
      await api.accountSessionClose(s.id);
      return 'Sessiya bağlandı.';
    });
  };

  const closeOthers = async () => {
    if (!(await confirm('Bu cihazdan başqa bütün cihazlardan çıxılsın?', { confirmLabel: 'Çıx', danger: true }))) return;
    void act(async () => {
      const r = await api.accountSessionsCloseOthers();
      return r.closed > 0 ? `${r.closed} cihazdan çıxıldı.` : 'Başqa açıq sessiya yox idi.';
    });
  };

  const others = items?.filter((s) => !s.current).length ?? 0;

  return (
    <section className="card wrap-any" aria-label="Açıq sessiyalar">
      <h2>Açıq sessiyalar</h2>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="ok" role="status">
          {notice}
        </p>
      )}
      {!items && !error && <p className="muted">Yüklənir…</p>}
      {items && (
        <ul className="product-list">
          {items.map((s) => (
            <li key={s.id} className="product-row">
              <span className="product-name">
                {s.device ?? 'Naməlum cihaz'}
                {s.current && <span className="tag">bu cihaz</span>}
                {s.remembered && <span className="tag">yadda saxlanıb</span>}
              </span>
              <span className="muted small">
                Giriş {when(s.createdAt)}
                {s.ip ? ` · ${s.ip}` : ''} · bitir {when(s.expiresAt)}
              </span>
              {!s.current && (
                <span className="toolbar">
                  <button type="button" className="link-btn" onClick={() => closeOne(s)} disabled={busy}>
                    Bağla
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <button type="button" className="btn-small" onClick={closeOthers} disabled={busy}>
          Bütün digər cihazlardan çıx
        </button>
      )}
    </section>
  );
}
